import { normalizarCnpj } from './normalizar-cnpj';

/**
 * Retorna a raiz do CNPJ: os primeiros 8 caracteres do CNPJ normalizado.
 *
 * A raiz identifica a empresa e é compartilhada por matriz e todas as filiais.
 *
 * @param valor - CNPJ com ou sem máscara, em qualquer capitalização.
 * @returns Os primeiros 8 caracteres do CNPJ normalizado.
 */
export function obterRaizCnpj(valor: string): string {
  return normalizarCnpj(valor).substring(0, 8);
}

/**
 * Retorna a ordem do CNPJ: os 4 caracteres nas posições 9 a 12 do CNPJ normalizado.
 *
 * O valor `0001` identifica a matriz; qualquer outro valor identifica uma filial.
 *
 * @param valor - CNPJ com ou sem máscara, em qualquer capitalização.
 * @returns Os 4 caracteres da ordem do CNPJ.
 */
export function obterOrdemCnpj(valor: string): string {
  return normalizarCnpj(valor).substring(8, 12);
}

/**
 * Retorna os dígitos verificadores do CNPJ: os 2 últimos caracteres.
 *
 * Os dígitos verificadores são sempre numéricos `[0-9]`, mesmo em CNPJs alfanuméricos.
 *
 * @param valor - CNPJ com ou sem máscara, em qualquer capitalização.
 * @returns Os 2 dígitos verificadores.
 */
export function obterDigitosVerificadoresCnpj(valor: string): string {
  return normalizarCnpj(valor).substring(12, 14);
}

/**
 * Verifica se o CNPJ corresponde a uma matriz (ordem igual a `0001`).
 *
 * @param valor - CNPJ com ou sem máscara, em qualquer capitalização.
 * @returns `true` se for matriz, `false` se for filial.
 */
export function ehMatriz(valor: string): boolean {
  return obterOrdemCnpj(valor) === '0001';
}

/**
 * Verifica se o CNPJ corresponde a uma filial (ordem diferente de `0001`).
 *
 * @param valor - CNPJ com ou sem máscara, em qualquer capitalização.
 * @returns `true` se for filial, `false` se for matriz.
 */
export function ehFilial(valor: string): boolean {
  return !ehMatriz(valor);
}

/**
 * Verifica se o CNPJ contém letras nas posições 1 a 12 (raiz + ordem).
 *
 * Os dígitos verificadores (posições 13-14) são sempre numéricos e não são
 * considerados nesta verificação.
 *
 * @param valor - CNPJ com ou sem máscara, em qualquer capitalização.
 * @returns `true` se o CNPJ for alfanumérico, `false` se for puramente numérico.
 */
export function contemLetras(valor: string): boolean {
  const normalizado = normalizarCnpj(valor);
  for (let i = 0; i < 12; i++) {
    const code = normalizado.charCodeAt(i);
    if (code >= 65 && code <= 90) return true; // A–Z
  }
  return false;
}

/**
 * Verifica se o CNPJ é puramente numérico (sem letras nas posições 1 a 12).
 *
 * @param valor - CNPJ com ou sem máscara, em qualquer capitalização.
 * @returns `true` se for puramente numérico, `false` se for alfanumérico.
 */
export function ehSomenteNumerico(valor: string): boolean {
  return !contemLetras(valor);
}

/**
 * Compara dois CNPJs lexicograficamente após normalização.
 *
 * Útil para ordenação de listas de CNPJs, independente de formato ou capitalização.
 *
 * @param cnpjA - Primeiro CNPJ, com ou sem máscara.
 * @param cnpjB - Segundo CNPJ, com ou sem máscara.
 * @returns `0` se iguais, valor negativo se `cnpjA` < `cnpjB`, positivo se `cnpjA` > `cnpjB`.
 */
export function compararCnpj(cnpjA: string, cnpjB: string): number {
  const a = normalizarCnpj(cnpjA);
  const b = normalizarCnpj(cnpjB);
  if (a < b) return -1;
  if (a > b) return 1;
  return 0;
}
